"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
const DEFAULT_MARKDOWN = `# Markdown to HTML Demo

Use this free tool to turn written content into polished HTML for README files, docs, blogs, and release notes.

## Why people use Markdown

- Keep content easy to read in plain text
- Publish clean HTML in a few seconds
- Work with tables, code blocks, and links

> Tip: Write the content first, then convert it to HTML when you're ready to paste it into a CMS or page template.

| Feature | Supports |
| --- | --- |
| Headings | Yes |
| Lists | Yes |
| Code blocks | Yes |
| Tables | Yes |

\`\`\`js
const greeting = "Hello from markdown!";
console.log(greeting);
\`\`\`

[Visit the docs](https://nextjs.org/docs)`;
const SANITIZE_CONFIG = {
  // Allow the everyday elements Markdown produces, nothing script-capable.
  ALLOWED_TAGS: ["a", "b", "blockquote", "br", "code", "del", "div", "em", "h1", "h2", "h3", "h4", "h5", "h6", "hr", "i", "img", "input", "li", "ol", "p", "pre", "s", "span", "strong", "sub", "sup", "table", "tbody", "td", "th", "thead", "tr", "ul"],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "class", "type", "checked", "disabled", "target", "rel"]
};
function sanitize(html: string): string {
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}
export default function MarkdownToHtml(t0) {
  const $ = _c(69);
  const [markdownInput, setMarkdownInput] = useState(DEFAULT_MARKDOWN);
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [copyLabel, setCopyLabel] = useState("Copy HTML");
  let t1;
  try {
    let t2;
    if ($[0] !== markdownInput) {
      const raw = marked.parse(markdownInput, {
        gfm: true,
        breaks: true
      }) as string;
      t2 = sanitize(raw);
      $[0] = markdownInput;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== t2) {
      t3 = {
        html: t2,
        parseError: ""
      };
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t1 = t3;
  } catch {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        html: "",
        parseError: "This Markdown couldn't be parsed. Check for an unclosed code block or table."
      };
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t2;
  }
  const {
    html,
    parseError
  } = t1;
  let t2;
  let t3;
  if ($[5] !== markdownInput) {
    const trimmed = markdownInput.trim();
    t2 = markdownInput.length;
    t3 = trimmed ? trimmed.split(/\s+/).length : 0;
    $[5] = markdownInput;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== markdownInput) {
    t4 = markdownInput === "" ? 0 : markdownInput.split("\n").length;
    $[8] = markdownInput;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      chars: t2,
      words: t3,
      lines: t4
    };
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const stats = t5;
  let t6;
  if ($[14] !== html) {
    t6 = async function copyHtml() {
      if (!html) {
        return;
      }
      try {
        await navigator.clipboard.writeText(html);
        setCopyLabel("Copied!");
      } catch {
        setCopyLabel("Copy failed");
      }
      window.setTimeout(() => setCopyLabel("Copy HTML"), 1500);
    };
    $[14] = html;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const copyHtml = t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function resetToDemo() {
      setMarkdownInput(DEFAULT_MARKDOWN);
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const resetToDemo = t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = function clearInput() {
      setMarkdownInput("");
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const clearInput = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div><p className="text-sm font-semibold text-slate-900">Live split-pane converter</p><p className="text-xs text-slate-500">Type Markdown on the left and preview the rendered HTML on the right.</p></div>;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = !showRawHtml;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setShowRawHtml(false);
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = `rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${showRawHtml ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "bg-indigo-600 text-white"}`;
  let t13;
  if ($[20] !== t10 || $[21] !== t12) {
    t13 = <button type="button" aria-pressed={t10} onClick={t11} className={t12}>Preview</button>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setShowRawHtml(true);
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = `rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${showRawHtml ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`;
  let t16;
  if ($[24] !== showRawHtml || $[25] !== t15) {
    t16 = <button type="button" aria-pressed={showRawHtml} onClick={t14} className={t15}>Raw HTML</button>;
    $[24] = showRawHtml;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t13 || $[28] !== t16) {
    t17 = <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">{t9}<div className="flex flex-wrap gap-2" role="group" aria-label="View mode">{t13}{t16}</div></div>;
    $[27] = t13;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <label htmlFor="markdown-input" className="text-sm font-medium text-slate-700">Markdown input</label>;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="mb-2 flex items-center justify-between">{t18}<div className="flex gap-2"><button type="button" onClick={resetToDemo} className="text-xs font-semibold text-slate-500 hover:text-slate-700">Reset to demo</button><span className="text-xs text-slate-300">·</span><button type="button" onClick={clearInput} className="text-xs font-semibold text-slate-500 hover:text-slate-700">Clear</button></div></div>;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = event => setMarkdownInput(event.target.value);
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== markdownInput) {
    t21 = <textarea id="markdown-input" value={markdownInput} onChange={t20} rows={18} spellCheck={false} className="w-full rounded-xl border border-slate-300 bg-white p-4 font-mono text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200" placeholder="Type or paste your Markdown here..." />;
    $[33] = markdownInput;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== stats.chars || $[36] !== stats.lines || $[37] !== stats.words) {
    t22 = <p className="mt-1 text-xs text-slate-500">{stats.words} words · {stats.chars} characters · {stats.lines} lines</p>;
    $[35] = stats.chars;
    $[36] = stats.lines;
    $[37] = stats.words;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t21 || $[40] !== t22) {
    t23 = <div>{t19}{t21}{t22}</div>;
    $[39] = t21;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const t24 = showRawHtml ? "Generated HTML" : "Rendered preview";
  let t25;
  if ($[42] !== t24) {
    t25 = <p className="text-sm font-semibold text-slate-900">{t24}</p>;
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== copyHtml) {
    t26 = () => void copyHtml();
    $[44] = copyHtml;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  const t27 = !html;
  let t28;
  if ($[46] !== copyLabel || $[47] !== t26 || $[48] !== t27) {
    t28 = <button type="button" onClick={t26} disabled={t27} className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300">{copyLabel}</button>;
    $[46] = copyLabel;
    $[47] = t26;
    $[48] = t27;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== t25 || $[51] !== t28) {
    t29 = <div className="mb-3 flex items-center justify-between gap-3">{t25}{t28}</div>;
    $[50] = t25;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== parseError) {
    t30 = parseError && <p role="alert" className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{parseError}</p>;
    $[53] = parseError;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  let t31;
  if ($[55] !== html || $[56] !== showRawHtml) {
    t31 = showRawHtml ? <textarea value={html} readOnly={true} rows={18} spellCheck={false} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-900" placeholder="HTML output will appear here." /> : html ? <div className="prose prose-slate max-w-none rounded-lg border border-slate-200 bg-white p-4 text-sm" dangerouslySetInnerHTML={{
      __html: html
    }} /> : <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white text-sm text-slate-500">Your preview will appear here.</div>;
    $[55] = html;
    $[56] = showRawHtml;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  let t32;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <p className="mt-2 text-xs text-slate-500">Output is sanitized: scripts and event handlers are stripped for your safety.</p>;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] !== t29 || $[60] !== t30 || $[61] !== t31) {
    t33 = <div className="rounded-xl border border-slate-300 bg-slate-50 p-4">{t29}{t30}{t31}{t32}</div>;
    $[59] = t29;
    $[60] = t30;
    $[61] = t31;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  let t34;
  if ($[63] !== t23 || $[64] !== t33) {
    t34 = <div className="grid gap-4 lg:grid-cols-2">{t23}{t33}</div>;
    $[63] = t23;
    $[64] = t33;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== t17 || $[67] !== t34) {
    t35 = <div className="space-y-4">{t17}{t34}</div>;
    $[66] = t17;
    $[67] = t34;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  return t35;
}